/**
 * English copy catalog for the commerce feature
 * (`@gamecodex/world-ui` → `features/commerce`).
 *
 * Keys mirror the `t('commerce.*')` calls in the feature's React components.
 * Values are the exact original English strings extracted during the i18n sweep.
 */
export const commerceEn = {
  // DtrpgClaimPage.tsx
  'commerce.dtrpg.seoTitle': 'Claim Your Library',
  'commerce.dtrpg.seoDescription': 'Import your DriveThruRPG library and claim your Grymwyrd PDFs.',
  'commerce.dtrpg.heading': 'Claim Your Library',
  'commerce.dtrpg.intro':
    'Bought Grymwyrd on DriveThruRPG or backed the campaign? Sign in with your purchase email to import your library and claim your copies.',
  'commerce.dtrpg.sendLinkError': 'We could not send your sign-in link right now. Try again shortly.',
  'commerce.dtrpg.checkInboxPrefix': 'Check your inbox at',
  'commerce.dtrpg.checkInboxSuffix':
    'If that email backed or bought Grymwyrd, a sign-in link is on its way — click it to return here and claim your library.',
  'commerce.dtrpg.linkAutoSignIn': 'The link signs you in automatically — no password needed.',
  'commerce.dtrpg.purchaseEmailLabel': 'Your purchase / pledge email',
  'commerce.dtrpg.sendingLink': 'Sending link…',
  'commerce.dtrpg.emailLinkCta': 'Email me a sign-in link',
  'commerce.dtrpg.signedInAs': 'Signed in as',
  'commerce.dtrpg.signOut': 'Sign out',
  'commerce.dtrpg.dtrpgEmailLabel': 'DriveThruRPG email',
  'commerce.dtrpg.dtrpgEmailOptional': '(optional — if different from above)',
  'commerce.dtrpg.dtrpgEmailHelp': 'We use this to match your DriveThruRPG purchases when granting your library.',
  'commerce.dtrpg.libraryHeading': 'Your Grymwyrd library',
  'commerce.dtrpg.noTitles': 'No importable titles are configured yet.',
  'commerce.dtrpg.claimNotInLibrary':
    'This title isn’t in your library yet. Make sure you used the right purchase email.',
  'commerce.dtrpg.claimSessionExpired': 'Your session expired — sign in again.',
  'commerce.dtrpg.claimError': 'Could not claim this title right now. Please try again.',
  'commerce.dtrpg.preparingCopy': 'Preparing your copy…',
  'commerce.dtrpg.download': 'Download',
  'commerce.dtrpg.preparing': 'Preparing…',
  'commerce.dtrpg.claiming': 'Claiming…',
  'commerce.dtrpg.claim': 'Claim',

  // PaywallPrompt.tsx
  'commerce.paywall.checkoutFailed': 'Checkout could not be opened. Please try again soon.',
  'commerce.paywall.signInToPurchase': 'Sign in to complete this purchase.',
  'commerce.paywall.vaultSealed': 'The merchant’s vault is sealed for now — please try again soon.',
  'commerce.paywall.signInToUnlock': 'Sign in to unlock',
  'commerce.paywall.comingSoonTitle': 'This purchase is coming soon',
  'commerce.paywall.priceComingSoon': '{price} — coming soon',
  'commerce.paywall.comingSoon': 'Coming soon',
  'commerce.paywall.openingVault': 'Opening the vault…',
  'commerce.paywall.unlockPrice': 'Unlock — {price}',
  'commerce.paywall.unlock': 'Unlock',

  // MarketplacePage.tsx
  'commerce.marketplace.installSuccess': 'Installed “{name}” onto your table.',
  'commerce.marketplace.installNotOwned': 'You don’t own this pack yet — purchasing packs is coming soon.',
  'commerce.marketplace.installHostOnly': 'Only the table host can install a pack.',
  'commerce.marketplace.installError': 'Could not install this pack. Please try again.',
  'commerce.marketplace.installed': 'Installed',
  'commerce.marketplace.installing': 'Installing…',
  'commerce.marketplace.installOnTable': 'Install on table',
  'commerce.marketplace.seoTitle': 'Marketplace — Packs',
  'commerce.marketplace.seoDescription':
    'Browse and install creator content packs — maps, scenes, handouts, and presets — onto your tables.',
  'commerce.marketplace.eyebrow': 'Marketplace',
  'commerce.marketplace.heading': 'Content Packs',
  'commerce.marketplace.intro':
    'Maps, scenes, handouts, and quick-start presets — install them onto your tables in a click.',
  'commerce.marketplace.installingOnto': 'Installing onto table {tableId}.',
  'commerce.marketplace.signInHeading': 'Sign in to see your packs',
  'commerce.marketplace.signInBlurb': 'The pack catalog is tied to your creator account.',
  'commerce.marketplace.signIn': 'Sign in',
  'commerce.marketplace.loading': 'Summoning your packs…',
  'commerce.marketplace.noPacksHeading': 'No packs yet',
  'commerce.marketplace.noPacksBlurb':
    'You haven’t published any packs. A public marketplace to browse other creators’ packs is coming soon.',
  'commerce.marketplace.footer': 'Browsing other creators’ packs and paid packs are on the way.',

  // SubscriberPerksPage.tsx
  'commerce.subscriber.perk1Title': 'Subscriber-only tables',
  'commerce.subscriber.perk1Blurb': 'Take a seat at tables your favorite hosts reserve for subscribers.',
  'commerce.subscriber.perk2Title': 'Early & exclusive content',
  'commerce.subscriber.perk2Blurb': 'Get campaign chapters, scenes, and handouts before anyone else.',
  'commerce.subscriber.perk3Title': 'Subscriber flair',
  'commerce.subscriber.perk3Blurb': 'A mark of standing on your profile and at the table.',
  'commerce.subscriber.seoTitle': 'Subscriber Perks',
  'commerce.subscriber.seoDescription':
    'Become a subscriber for exclusive tables, early content, and subscriber flair.',
  'commerce.subscriber.heading': 'Become a Subscriber',
  'commerce.subscriber.intro':
    'Support the hosts and creators you love — and unlock the perks they reserve for their inner coven.',
  'commerce.subscriber.activeHeading': 'You’re a subscriber',
  'commerce.subscriber.activeBlurb':
    'Your perks are active. Look for the subscriber crest on tables you can join.',
  'commerce.subscriber.findTable': 'Find a subscriber table',
  'commerce.subscriber.paywallTitle': 'Grymwyrd Subscriber',
  'commerce.subscriber.paywallBlurb':
    'Unlock subscriber-only tables, early content, and your subscriber flair.',
  'commerce.subscriber.paywallPrice': 'from $5/mo',

  // TicketCheckoutPage.tsx
  'commerce.ticket.checkoutFailed': 'Checkout could not be opened. Please try again soon.',
  'commerce.ticket.signInToBuy': 'Sign in to buy a seat at this table.',
  'commerce.ticket.boxOfficeClosed': 'The box office is closed for now — please try again soon.',
  'commerce.ticket.seoTitle': 'Buy a Seat',
  'commerce.ticket.seoDescription': 'Reserve your seat at a ticketed table.',
  'commerce.ticket.eyebrow': 'Ticketed Table',
  'commerce.ticket.heading': 'Reserve Your Seat',
  'commerce.ticket.loading': 'Loading the table…',
  'commerce.ticket.notFoundHeading': 'Table not found',
  'commerce.ticket.notFoundBlurb': 'This table may have closed, or its doors were never opened.',
  'commerce.ticket.backToLobby': 'Back to the lobby',
  'commerce.ticket.freeHeading': 'This table is free to join',
  'commerce.ticket.freeBlurb': 'No ticket required — pull up a chair.',
  'commerce.ticket.goToLobby': 'Go to the lobby',
  'commerce.ticket.untitledTable': 'Untitled table',
  'commerce.ticket.seatPrice': 'Seat price',
  'commerce.ticket.spectatorsNote': 'Spectators must also hold a ticket for this table.',
  'commerce.ticket.paymentReceived': 'Payment received — your seat is reserved. Head to the lobby to take it.',
  'commerce.ticket.signInToBuySeat': 'Sign in to buy a seat',
  'commerce.ticket.openingBoxOffice': 'Opening the box office…',
  'commerce.ticket.buySeatPrice': 'Buy seat — {price}',
  'commerce.ticket.comingSoonTitle': 'Ticketing is coming soon',
  'commerce.ticket.ticketingComingSoon': 'Ticketing coming soon',
  'commerce.ticket.comingSoonFooter': 'Paid seats are almost ready. For now, ask the host for a free invite.',

  // TipGoalManagerPage.tsx
  'commerce.tip.errHostOnlyGoal': 'Only the table host can manage goals.',
  'commerce.tip.errHostOnlyBounty': 'Only the table host can manage bountys.',
  'commerce.tip.errMaxGoal': 'You’ve reached the maximum number of goals for this table.',
  'commerce.tip.errMaxBounty': 'You’ve reached the maximum number of bountys for this table.',
  'commerce.tip.errMissingGoal': 'That goal is missing required details.',
  'commerce.tip.errMissingBounty': 'That bounty is missing required details.',
  'commerce.tip.errSignIn': 'Sign in as the host to manage this table.',
  'commerce.tip.errSaveFailedGoal': 'Could not save the goal. Please try again.',
  'commerce.tip.errSaveFailedBounty': 'Could not save the bounty. Please try again.',
  'commerce.tip.goalValidation': 'Add a label and a positive dollar target.',
  'commerce.tip.goalAdded': 'Goal “{label}” added.',
  'commerce.tip.goalLabelPlaceholder': 'Goal label (e.g. Summon the boss)',
  'commerce.tip.targetPlaceholder': 'Target $',
  'commerce.tip.cuePlaceholder': 'Cue when reached (e.g. fanfare)',
  'commerce.tip.addGoal': 'Add goal',
  'commerce.tip.bountyValidation': 'Add a label, a positive target, and a trigger.',
  'commerce.tip.bountyAdded': 'Bounty “{label}” added.',
  'commerce.tip.bountyLabelPlaceholder': 'Bounty label (e.g. Nat 20 saves the party)',
  'commerce.tip.triggerPlaceholder': 'Trigger (e.g. a natural 20)',
  'commerce.tip.addBounty': 'Add bounty',
  'commerce.tip.signInToManage': 'Sign in to manage this table.',
  'commerce.tip.bountyClaimed': 'Bounty “{label}” claimed.',
  'commerce.tip.seoTitle': 'Tip Goals & Bounties',
  'commerce.tip.seoDescription': 'Set tip goals and bounties for your table.',
  'commerce.tip.backToLobby': 'Back to lobby',
  'commerce.tip.heading': 'Tip Goals & Bounties',
  'commerce.tip.intro':
    'Set goals and bounties for your table. When the cup runs over, the cue fires for your audience. The on-stream display is handled by your broadcast overlay.',
  'commerce.tip.loading': 'Reading the ledger…',
  'commerce.tip.loadErrorTitle': 'Could not load the ledger',
  'commerce.tip.raisedSoFar': 'Raised so far:',
  'commerce.tip.tipGoalsHeading': 'Tip Goals',
  'commerce.tip.noGoals': 'No goals yet — add your first below.',
  'commerce.tip.reachedAria': 'reached',
  'commerce.tip.bountiesHeading': 'Bounties',
  'commerce.tip.noBounties': 'No bounties yet — add your first below.',
  'commerce.tip.whenLabel': '· when: {trigger}',
  'commerce.tip.claimed': 'Claimed',
  'commerce.tip.claimTitleReady': 'Mark this bounty claimed',
  'commerce.tip.claimTitleNotFunded': 'Not yet funded',
  'commerce.tip.claim': 'Claim',
} as const
